import { camelCase, kebabCase } from 'lodash';
import { first } from './first';
import { HttpMethod } from './HttpMethod';
import { ServiceAction } from './ServiceAction';

const serviceActionToHttpMethodList = Object.entries({
  [ServiceAction.fetch]: HttpMethod.GET,
  [ServiceAction.post]: HttpMethod.POST,
  [ServiceAction.put]: HttpMethod.PUT,
  [ServiceAction.delete]: HttpMethod.DELETE,
  [ServiceAction.run]: HttpMethod.POST,
});

const methodNamePartSplitRegex = /(By|For)(?=[A-Z])/;

export function restSpecFromMethodName(methodName: string): RestSpec | undefined {
  const serviceActionAndHttpMethod = first(
    serviceActionToHttpMethodList,
    ([serviceAction]) => methodName.startsWith(serviceAction),
  );

  if (serviceActionAndHttpMethod === undefined) {
    return undefined; // not a service method
  }

  const [serviceAction, httpMethod] = serviceActionAndHttpMethod;
  const methodNameParts = methodName.substring(serviceAction.length).split(methodNamePartSplitRegex);

  const propsIntegrated = [] as string[];
  const pathSpecSegments = [] as string[];
  const propNames = [] as string[];

  // first pass is to combine props and their corresponding segments
  // Process the pattern: ResourceByParam1ByParam2 -> /resource/:param1/:param2
  let resourceName = '';
  let i = 0;
  
  while (i < methodNameParts.length) {
    const part = methodNameParts[i];

    // Skip 'By' and 'For' separators - but check if the NEXT part after 'By' is a parameter
    if (part === 'By') {
      // This 'By' indicates the next part is a parameter
      if (i + 1 < methodNameParts.length) {
        const paramPart = methodNameParts[i + 1];
        const propName = camelCase(paramPart);
        propNames.push(propName);
        // Additional parameters just add `/:paramName`
        propsIntegrated.push(`/:${propName}`);
        i += 2; // Skip 'By' and parameter name
        continue;
      }
      i++;
      continue;
    }
    
    if (part === 'For') {
      // Add 'for' to propsIntegrated so the second loop can handle prepending
      propsIntegrated.push('for');
      i++;
      continue;
    }

    // Check if this part is followed by 'For' (indicating prepending comes after)
    if (i < methodNameParts.length - 1 && methodNameParts[i + 1] === 'For') {
      // The segment after 'For' should be prepended
      if (i + 2 < methodNameParts.length) {
        const forPart = methodNameParts[i + 2];
        // Add the current part as a regular segment, then 'for', then the part after 'For'
        if (!resourceName) {
          resourceName = kebabCase(part);
        }
        propsIntegrated.push(kebabCase(part));
        propsIntegrated.push('for');
        // Check if the part after 'For' is followed by 'By' (parameter)
        if (i + 3 < methodNameParts.length && methodNameParts[i + 3] === 'By' && i + 4 < methodNameParts.length) {
          const paramPart = methodNameParts[i + 4];
          const propName = camelCase(paramPart);
          propNames.push(propName);
          propsIntegrated.push(`${kebabCase(forPart)}/:${propName}`);
          i += 5; // Skip current, 'For', forPart, 'By', and parameter
        } else {
          propsIntegrated.push(kebabCase(forPart));
          i += 3; // Skip current part, 'For' (i+1), and the part after 'For' (i+2)
        }
        continue;
      }
    }

    // Check if this part is followed by 'By' (indicating a parameter comes after)
    if (i < methodNameParts.length - 1 && methodNameParts[i + 1] === 'By') {
      // The parameter name is the part AFTER 'By', which is at index i+2
      if (i + 2 < methodNameParts.length) {
        const paramPart = methodNameParts[i + 2];
        const propName = camelCase(paramPart);
        propNames.push(propName);
        // If we haven't set the resource name yet, this part (before 'By') IS the resource name
        if (!resourceName) {
          resourceName = kebabCase(part);
          propsIntegrated.push(`${resourceName}/:${propName}`);
        } else {
          // Additional parameters just add `/:paramName`
          propsIntegrated.push(`/:${propName}`);
        }
        i += 3; // Skip current part, 'By' (i+1), and parameter name (i+2), move to i+3
        continue;
      }
    }

    // Regular segment (not followed by 'By' or 'For') - this is the resource name if not set
    // This handles cases where there's no 'By' or 'For' at all, or trailing segments
    if (!resourceName) {
      resourceName = kebabCase(part);
    }
    propsIntegrated.push(kebabCase(part));
    i++;
  }

  // now put in proper order based on 'for', which is now in kebab-case, so lowercase 'for'
  let nextIsPrepend = false; // `For` triggers prepending, so `fetchSurveyForClient` translates to `/client/survey`
  for (let i = 0; i < propsIntegrated.length; i++) {
    const part = propsIntegrated[i];

    if (part === 'for') {
      nextIsPrepend = true;
      continue;
    }
    if (nextIsPrepend) {
      pathSpecSegments.unshift(part);
    } else {
      pathSpecSegments.push(part);
    }
    nextIsPrepend = false;
  }

  // Filter out empty strings and normalize segments (remove leading slashes to prevent double slashes)
  const filteredSegments = pathSpecSegments
    .filter(seg => seg !== '' && seg !== '/')
    .map(seg => seg.startsWith('/') ? seg.substring(1) : seg);
  const pathSpec = `/${ filteredSegments.join('/') }`;

  return {
    methodName,
    httpMethod,
    pathSpec,
    propNames,
    serviceAction,
  };
}
